import { useEffect, useState } from 'react';

/** L'événement que Chrome et Edge émettent quand le site peut s'installer. */
type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

/**
 * Installer le site comme une application (écran d'accueil, fenêtre à part) :
 * `install` est défini seulement quand le navigateur le permet et que ce n'est
 * pas déjà fait. Safari n'émet rien : on passe alors par le menu Partager.
 */
export function useInstallPrompt(): { install: (() => Promise<void>) | null } {
  const [event, setEvent] = useState<BeforeInstallPromptEvent | null>(null);

  useEffect(() => {
    const onPrompt = (incoming: Event) => {
      incoming.preventDefault();
      setEvent(incoming as BeforeInstallPromptEvent);
    };
    const onInstalled = () => setEvent(null);
    window.addEventListener('beforeinstallprompt', onPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  return {
    install: event
      ? async () => {
          await event.prompt();
          await event.userChoice;
          setEvent(null);
        }
      : null,
  };
}
